import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ArrowLeft, 
  Save,
  Loader2,
  Package,
  AlertCircle,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const InitialStockPosting = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [plants, setPlants] = useState([]);
  const [storageLocations, setStorageLocations] = useState([]);
  const [filteredSLocs, setFilteredSLocs] = useState([]);
  
  const [formData, setFormData] = useState({
    material_id: '',
    plant: '',
    storage_location: '',
    quantity: '',
    unit_of_measure: 'EA',
    unit_price: '',
    currency: 'USD',
    stock_type: 'UNRESTRICTED',
    posting_date: new Date().toISOString().split('T')[0],
    document_date: new Date().toISOString().split('T')[0],
    header_text: 'Initial Stock Posting',
    reference: '',
    created_by: 'ADMIN'
  });
  
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    loadMasterData();
  }, []);

  useEffect(() => {
    if (formData.plant) {
      const filtered = storageLocations.filter(s => s.plant_id === formData.plant);
      setFilteredSLocs(filtered);
      if (!filtered.find(s => s.storage_location_id === formData.storage_location)) {
        setFormData(prev => ({ ...prev, storage_location: '' }));
      }
    }
  // eslint-disable-next-line
  }, [formData.plant, storageLocations]);

  const loadMasterData = async () => {
    try {
      setLoading(true);
      const [matRes, plantRes, slocRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/materials`),
        axios.get(`${BACKEND_URL}/api/material-management/plants`),
        axios.get(`${BACKEND_URL}/api/material-management/storage-locations`)
      ]);
      setMaterials(matRes.data || []);
      setPlants(plantRes.data || []);
      setStorageLocations(slocRes.data || []);
    } catch (error) {
      console.error('Error loading master data:', error);
      toast.error('Failed to load master data');
    } finally {
      setLoading(false);
    }
  };

  const handleMaterialChange = (materialId) => {
    setFormData(prev => ({ ...prev, material_id: materialId }));
    const material = materials.find(m => m.material_number === materialId);
    setSelectedMaterial(material);
    if (material) {
      setFormData(prev => ({
        ...prev,
        unit_of_measure: material.base_uom || 'EA',
        unit_price: material.accounting_data?.moving_avg_price || material.accounting_data?.standard_price || ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.material_id || !formData.plant || !formData.storage_location) {
      toast.error('Please fill all required fields');
      return;
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      toast.error('Quantity must be greater than zero');
      return;
    }
    if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
      toast.error('Unit price must be greater than zero');
      return;
    }

    try {
      setSubmitting(true);
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unit_price: parseFloat(formData.unit_price)
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/initial-stock`, payload);
      setResult(response.data);
      toast.success(`Stock posted successfully! Document: ${response.data.material_document}`);
    } catch (error) {
      console.error('Error posting stock:', error);
      toast.error(error.response?.data?.detail || 'Failed to post stock');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setFormData({
      material_id: '',
      plant: '',
      storage_location: '',
      quantity: '',
      unit_of_measure: 'EA',
      unit_price: '',
      currency: 'USD',
      stock_type: 'UNRESTRICTED',
      posting_date: new Date().toISOString().split('T')[0],
      document_date: new Date().toISOString().split('T')[0],
      header_text: 'Initial Stock Posting',
      reference: '',
      created_by: 'ADMIN'
    });
    setSelectedMaterial(null);
    setResult(null);
  };

  const calculateValue = () => {
    const qty = parseFloat(formData.quantity) || 0;
    const price = parseFloat(formData.unit_price) || 0;
    return qty * price;
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-orange-600" />
          <span className="ml-2">Loading master data...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate('/inventory')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Initial Stock Posting</h1>
            <p className="text-sm text-gray-500">MB1C - Movement Type 561</p>
          </div>
        </div>

        {/* Success Result */}
        {result && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-green-800">Stock Posted Successfully</h3>
                <div className="mt-2 text-sm text-green-700 space-y-1">
                  <p><strong>Material Document:</strong> {result.material_document}</p>
                  <p><strong>Fiscal Year:</strong> {result.fiscal_year}</p>
                  <p><strong>Posting Date:</strong> {result.posting_date}</p>
                  <p><strong>Total Amount:</strong> ${result.total_amount?.toFixed(2)}</p>
                </div>
                <div className="mt-3 flex gap-2">
                  <Button size="sm" variant="outline" onClick={handleReset}>
                    Post Another
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => navigate('/inventory/stock-overview')}>
                    View Stock
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Material Selection */}
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Material *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.material_id}
                  onChange={(e) => handleMaterialChange(e.target.value)}
                  required
                >
                  <option value="">Select Material</option>
                  {materials.map(m => (
                    <option key={m.material_number} value={m.material_number}>
                      {m.material_number} - {m.description}
                    </option>
                  ))}
                </select>
              </div>

              {/* Material Info Card */}
              {selectedMaterial && (
                <div className="md:col-span-2 bg-orange-50 rounded-lg p-4 border border-orange-200">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <span className="text-orange-600">Material Type:</span>
                      <p className="font-medium">{selectedMaterial.material_type}</p>
                    </div>
                    <div>
                      <span className="text-orange-600">Base UoM:</span>
                      <p className="font-medium">{selectedMaterial.base_uom}</p>
                    </div>
                    <div>
                      <span className="text-orange-600">Material Group:</span>
                      <p className="font-medium">{selectedMaterial.material_group || '-'}</p>
                    </div>
                    <div>
                      <span className="text-orange-600">Industry Sector:</span>
                      <p className="font-medium">{selectedMaterial.industry_sector || '-'}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Plant */}
              <div>
                <Label className="text-sm font-medium">Plant *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.plant}
                  onChange={(e) => setFormData({ ...formData, plant: e.target.value })}
                  required
                >
                  <option value="">Select Plant</option>
                  {plants.map(p => (
                    <option key={p.plant_id} value={p.plant_id}>
                      {p.plant_id} - {p.plant_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Storage Location */}
              <div>
                <Label className="text-sm font-medium">Storage Location *</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.storage_location}
                  onChange={(e) => setFormData({ ...formData, storage_location: e.target.value })}
                  required
                  disabled={!formData.plant}
                >
                  <option value="">Select Storage Location</option>
                  {filteredSLocs.map(s => (
                    <option key={s.storage_location_id} value={s.storage_location_id}>
                      {s.storage_location_id} - {s.name || s.description || 'No Description'}
                    </option>
                  ))}
                </select>
              </div>

              {/* Quantity */}
              <div>
                <Label className="text-sm font-medium">Quantity *</Label>
                <Input
                  type="number"
                  step="0.001"
                  min="0.001"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                  placeholder="Enter quantity"
                  required
                  className="mt-1"
                />
              </div>

              {/* Unit of Measure */}
              <div>
                <Label className="text-sm font-medium">Unit of Measure</Label>
                <Input
                  value={formData.unit_of_measure}
                  onChange={(e) => setFormData({ ...formData, unit_of_measure: e.target.value })}
                  placeholder="EA"
                  className="mt-1"
                />
              </div>

              {/* Unit Price */}
              <div>
                <Label className="text-sm font-medium">Unit Price *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })}
                  placeholder="Enter unit price"
                  required
                  className="mt-1"
                />
              </div>

              {/* Currency */}
              <div>
                <Label className="text-sm font-medium">Currency</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                  <option value="INR">INR</option>
                </select>
              </div>

              {/* Stock Type */}
              <div>
                <Label className="text-sm font-medium">Stock Type</Label>
                <select
                  className="w-full border rounded-md px-3 py-2 mt-1"
                  value={formData.stock_type}
                  onChange={(e) => setFormData({ ...formData, stock_type: e.target.value })}
                >
                  <option value="UNRESTRICTED">Unrestricted</option>
                  <option value="QUALITY">Quality Inspection</option>
                  <option value="BLOCKED">Blocked</option>
                </select>
              </div>

              {/* Posting Date */}
              <div>
                <Label className="text-sm font-medium">Posting Date *</Label>
                <Input
                  type="date"
                  value={formData.posting_date}
                  onChange={(e) => setFormData({ ...formData, posting_date: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>

              {/* Document Date */}
              <div>
                <Label className="text-sm font-medium">Document Date</Label>
                <Input
                  type="date"
                  value={formData.document_date}
                  onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
                  className="mt-1"
                />
              </div>

              {/* Reference */}
              <div>
                <Label className="text-sm font-medium">Reference</Label>
                <Input
                  value={formData.reference}
                  onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                  placeholder="External reference"
                  className="mt-1"
                />
              </div>

              {/* Header Text */}
              <div className="md:col-span-2">
                <Label className="text-sm font-medium">Header Text</Label>
                <Input
                  value={formData.header_text}
                  onChange={(e) => setFormData({ ...formData, header_text: e.target.value })}
                  placeholder="Description"
                  className="mt-1"
                />
              </div>
            </div>

            {/* Calculated Value */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Calculated Stock Value:</span>
                <span className="text-2xl font-bold text-green-600">
                  ${calculateValue().toFixed(2)} {formData.currency}
                </span>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="mt-6 flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleReset}>
                Reset
              </Button>
              <Button type="submit" disabled={submitting} className="bg-green-600 hover:bg-green-700">
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Posting...</>
                ) : (
                  <><Save className="h-4 w-4 mr-2" /> Post Stock</>                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default InitialStockPosting;
